import { jsxs as _jsxs } from "react/jsx-runtime";
// DraggableWrapper.tsx
import React, { useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
export function DraggableWrapper({ id, position, children, pageZoom, onDragPosition, customBounds, }) {
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id,
    });
    const nodeRef = useRef(null);
    const parentRef = useRef(null);
    const combinedRef = (node) => {
        nodeRef.current = node;
        setNodeRef(node);
        if (node) {
            parentRef.current = node.parentElement;
        }
    };
    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
    // -----------------------------------
    // transform -> document coordinates
    // -----------------------------------
    let adjustedX = transform ? transform.x / pageZoom : 0;
    let adjustedY = transform ? transform.y / pageZoom : 0;
    // -----------------------------------
    // bounds clamp
    // -----------------------------------
    if (nodeRef.current && parentRef.current) {
        const parentRect = parentRef.current.getBoundingClientRect();
        const nodeRect = nodeRef.current.getBoundingClientRect();
        // rects are already scaled
        // -> unscale
        const parentWidth = parentRect.width / pageZoom;
        const parentHeight = parentRect.height / pageZoom;
        const nodeWidth = nodeRect.width / pageZoom;
        const nodeHeight = nodeRect.height / pageZoom;
        const maxX = parentWidth - nodeWidth;
        const maxY = parentHeight - nodeHeight;
        // -----------------------------------
        // default parent bounds
        // -----------------------------------
        let minX = -position.x;
        let minY = -position.y;
        let maxAllowedX = maxX - position.x;
        let maxAllowedY = maxY - position.y;
        // -----------------------------------
        // apply custom bounds
        // -----------------------------------
        if (customBounds) {
            // LEFT
            if (customBounds.left !== undefined) {
                minX = Math.max(minX, customBounds.left - position.x);
            }
            // RIGHT
            if (customBounds.right !== undefined) {
                maxAllowedX = Math.min(maxAllowedX, customBounds.right - position.x);
            }
            // TOP
            if (customBounds.top !== undefined) {
                minY = Math.max(minY, customBounds.top - position.y);
            }
            // BOTTOM
            if (customBounds.bottom !== undefined) {
                maxAllowedY = Math.min(maxAllowedY, customBounds.bottom - position.y);
            }
        }
        // -----------------------------------
        // final clamp
        // -----------------------------------
        adjustedX = clamp(adjustedX, minX, maxAllowedX);
        adjustedY = clamp(adjustedY, minY, maxAllowedY);
    }
    // -----------------------------------
    // Report final drag position
    // -----------------------------------
    onDragPosition === null || onDragPosition === void 0 ? void 0 : onDragPosition(id, adjustedX, adjustedY);
    const style = {
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: CSS.Translate.toString({
            x: adjustedX,
            y: adjustedY,
            scaleX: 1,
            scaleY: 1,
        }),
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        zIndex: transform ? 999 : 10,
        touchAction: 'none',
    };
    const [handle, content] = children;
    return (_jsxs("div", { ref: combinedRef, className: "react-draggable", style: style, children: [React.cloneElement(handle, Object.assign(Object.assign({}, listeners), attributes)), content] }));
}
